import React from "react";
import { Link, useOutletContext } from "react-router-dom";
import { AlertCircle } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@pgs-core/ui/components/alert";
import { PageHeader } from "@pgs-core/ui/components/page-header";
import { SectionTabs, type SectionTab } from "@pgs-core/ui/components/section-tabs";
import type { ClientOutletContext } from "../lib/outlet-context";

// The booking module's tabs — exact matches so deeper routes leave them unlit.
const BOOKING_TABS: SectionTab[] = [
  { label: "Calendar", href: "/bookings", end: true },
  { label: "Services", href: "/bookings/services", end: true },
  { label: "Blocked times", href: "/bookings/blocked", end: true },
  { label: "Settings", href: "/bookings/settings", end: true },
];

/** The booking module's sticky tab header; `action` renders right-aligned. */
export function BookingModuleHeader({ action }: { action?: React.ReactNode }) {
  return <SectionTabs sticky items={BOOKING_TABS} action={action} />;
}

/**
 * Gate wrapper for the booking module pages: renders children only when the
 * module is active, otherwise the Add-ons upsell pointer (same pattern as
 * MenuModuleGuard).
 */
export function BookingModuleGuard({ title, description, children }: {
  title: React.ReactNode;
  description?: React.ReactNode;
  children: (ctx: ClientOutletContext) => React.ReactNode;
}) {
  const ctx = useOutletContext<ClientOutletContext>();
  const moduleActive =
    Array.isArray(ctx.activeModules) && ctx.activeModules.includes("booking");

  if (!moduleActive) {
    return (
      <div>
        <PageHeader title={title} description={description} />
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>The bookings module isn't active for your site</AlertTitle>
          <AlertDescription>
            See what it includes on the <Link className="underline" to="/modules">Add-ons</Link> page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return children(ctx);
}
